import { TAP_MAX_MOVE_PX } from "../constants";
import { indexToTime, timeToIndex } from "./timeIndex";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// The pointer may leave the pane during a drag, the drawing stays reachable
const clampToPane = ({ x, y }, { width, height }) => ({ x: clamp(x, 0, width), y: clamp(y, 0, height) });

const createMoveThreshold = (start) => {
  let isMoved = false;

  return {
    get isMoved() {
      return isMoved;
    },

    check: (point) => {
      isMoved = isMoved || Math.hypot(point.x - start.x, point.y - start.y) >= TAP_MAX_MOVE_PX;

      return isMoved;
    },
  };
};

// A press that does not move is a tap, it leaves the drawing where it is
const createDrag = ({ item, start, pane, onChange, onCommit }, pointsAt) => {
  const originalPoints = item.points;
  const threshold = createMoveThreshold(start);

  return {
    move: (point) => {
      if (!threshold.check(point)) return;

      const nextPoints = pointsAt(clampToPane(point, pane));

      if (nextPoints) {
        item.points = nextPoints;
        onChange();
      }
    },

    end: ({ isCancelled }) => {
      if (isCancelled) item.points = originalPoints;
      // A save during the drag may hold the moved points, so a cancelled drag is a change too
      if (threshold.isMoved) onCommit();
    },
  };
};

/**
 * Drag of one handle: the point goes where the pointer is (with grid snap).
 *
 * @param {object} params
 * @param {object} params.item - the drawing, its points are replaced
 * @param {number} params.pointIndex
 * @param {{ x: number, y: number }} params.start - CSS px of the pane
 * @param {{ width: number, height: number }} params.pane
 * @param {(pixel: object) => object|null} params.toPoint - pixelToPoint with the current view
 * @param {() => void} params.onChange - schedules a frame
 * @param {() => void} params.onCommit - the drag has ended, with the drawing moved or moved back
 * @returns {{ move: Function, end: Function }}
 */
export const createPointDrag = ({ pointIndex, toPoint, ...drag }) =>
  createDrag(drag, (pixel) => {
    const point = toPoint(pixel);

    return point && drag.item.points.map((current, index) => (index === pointIndex ? point : current));
  });

/**
 * Drag of the whole drawing: all points shift by the same bars and price.
 * The shifts are counted from the time and price under the press, so bars
 * that arrive during the drag do not move it. Grid snap does not apply: the
 * shape must not change.
 *
 * @param {object} params
 * @param {object} params.item - the drawing, its points are replaced
 * @param {{ x: number, y: number }} params.start - CSS px of the pane
 * @param {{ width: number, height: number }} params.pane
 * @param {object} params.view - see createDrawings
 * @param {() => void} params.onChange - schedules a frame
 * @param {() => void} params.onCommit - the drag has ended, with the drawing moved or moved back
 * @returns {{ move: Function, end: Function }}
 */
export const createShapeDrag = ({ view, ...drag }) => {
  const { store, barDurationMs, timeScale, priceScale } = view;
  const startTime = indexToTime(timeScale.xToIndex(drag.start.x), store, barDurationMs);
  const startPrice = priceScale.yToPrice(drag.start.y);
  const originalPoints = drag.item.points;

  return createDrag(drag, ({ x, y }) => {
    if (!store.length) return null;

    const barShift = timeScale.xToIndex(x) - timeToIndex(startTime, store, barDurationMs);
    const priceShift = priceScale.yToPrice(y) - startPrice;

    return originalPoints.map(({ time, price }) => ({
      time: indexToTime(timeToIndex(time, store, barDurationMs) + barShift, store, barDurationMs),
      price: price + priceShift,
    }));
  });
};
